package org.example.project.ui.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

enum class BannerType {
    SUCCESS, ERROR, WARNING, INFO
}

@Composable
fun LoadingState(
    message: String = "Ładowanie danych...",
    modifier: Modifier = Modifier
) {
    val shapes = LocalPlayfulShapes.current
    Surface(
        color = PlayfulColors.Surface,
        shape = shapes.card,
        border = BorderStroke(1.dp, PlayfulColors.BorderSubtle),
        modifier = modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 40.dp, horizontal = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            CircularProgressIndicator(
                color = PlayfulColors.Coral,
                strokeWidth = 3.dp,
                modifier = Modifier.size(36.dp)
            )
            Spacer(modifier = Modifier.height(14.dp))
            Text(
                text = message,
                fontSize = 14.sp,
                fontWeight = FontWeight.Medium,
                color = PlayfulColors.TextSecondary
            )
        }
    }
}

@Composable
fun EmptyState(
    title: String,
    description: String,
    modifier: Modifier = Modifier,
    icon: ImageVector? = null,
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null
) {
    val shapes = LocalPlayfulShapes.current
    Surface(
        color = PlayfulColors.Surface,
        shape = shapes.card,
        border = BorderStroke(1.dp, PlayfulColors.BorderSubtle),
        modifier = modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = 44.dp, horizontal = 24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Surface(
                color = PlayfulColors.SurfaceVariant,
                shape = shapes.pill,
                modifier = Modifier.size(52.dp)
            ) {
                Column(
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Icon(
                        imageVector = icon ?: AppIcons.Catalog,
                        contentDescription = null,
                        tint = PlayfulColors.Coral,
                        modifier = Modifier.size(24.dp)
                    )
                }
            }
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = title,
                fontSize = 17.sp,
                fontWeight = FontWeight.Bold,
                color = PlayfulColors.TextPrimary,
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(6.dp))
            Text(
                text = description,
                fontSize = 13.sp,
                color = PlayfulColors.TextSecondary,
                textAlign = TextAlign.Center,
                modifier = Modifier.padding(horizontal = 16.dp)
            )
            if (actionLabel != null && onAction != null) {
                Spacer(modifier = Modifier.height(20.dp))
                PrimaryButton(
                    text = actionLabel,
                    onClick = onAction
                )
            }
        }
    }
}

@Composable
fun ErrorState(
    title: String,
    message: String,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null
) {
    val shapes = LocalPlayfulShapes.current
    Surface(
        color = PlayfulColors.ErrorContainer,
        shape = shapes.card,
        border = BorderStroke(1.dp, PlayfulColors.ErrorBorder),
        modifier = modifier.fillMaxWidth()
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            Icon(
                imageVector = AppIcons.Warning,
                contentDescription = null,
                tint = PlayfulColors.Error,
                modifier = Modifier.size(32.dp)
            )
            Spacer(modifier = Modifier.height(12.dp))
            Text(
                text = title,
                fontSize = 16.sp,
                fontWeight = FontWeight.Bold,
                color = PlayfulColors.OnErrorContainer,
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.height(6.dp))
            Text(
                text = message,
                fontSize = 13.sp,
                color = PlayfulColors.OnErrorContainer,
                textAlign = TextAlign.Center
            )
            if (onRetry != null) {
                Spacer(modifier = Modifier.height(16.dp))
                SecondaryButton(
                    text = "Spróbuj ponownie",
                    onClick = onRetry
                )
            }
        }
    }
}

@Composable
fun FeedbackBanner(
    message: String,
    type: BannerType,
    modifier: Modifier = Modifier,
    title: String? = null,
    onDismiss: (() -> Unit)? = null,
    actionLabel: String? = null,
    onAction: (() -> Unit)? = null
) {
    val shapes = LocalPlayfulShapes.current
    val (bg, fg, border, icon) = when (type) {
        BannerType.SUCCESS -> listOf(
            PlayfulColors.SuccessContainer,
            PlayfulColors.OnSuccessContainer,
            PlayfulColors.SuccessBorder,
            AppIcons.Check
        )
        BannerType.ERROR -> listOf(
            PlayfulColors.ErrorContainer,
            PlayfulColors.OnErrorContainer,
            PlayfulColors.ErrorBorder,
            AppIcons.Warning
        )
        BannerType.WARNING -> listOf(
            PlayfulColors.WarningContainer,
            PlayfulColors.OnWarningContainer,
            PlayfulColors.WarningBorder,
            AppIcons.Warning
        )
        BannerType.INFO -> listOf(
            PlayfulColors.InfoContainer,
            PlayfulColors.OnInfoContainer,
            PlayfulColors.InfoBorder,
            AppIcons.Catalog
        )
    }

    Surface(
        color = bg as Color,
        shape = shapes.cardSmall,
        border = BorderStroke(1.dp, border as Color),
        modifier = modifier.fillMaxWidth()
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Icon(
                    imageVector = icon as ImageVector,
                    contentDescription = null,
                    tint = fg as Color,
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(10.dp))
                Column {
                    if (title != null) {
                        Text(
                            text = title,
                            color = fg,
                            fontSize = 13.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                    Text(
                        text = message,
                        color = fg,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.Medium
                    )
                }
            }
            if (actionLabel != null && onAction != null) {
                Spacer(modifier = Modifier.width(8.dp))
                SecondaryButton(
                    text = actionLabel,
                    onClick = onAction,
                    modifier = Modifier.height(34.dp)
                )
            }
            if (onDismiss != null) {
                IconButton(
                    onClick = onDismiss,
                    modifier = Modifier.size(28.dp)
                ) {
                    Icon(
                        imageVector = AppIcons.Close,
                        contentDescription = "Zamknij",
                        tint = fg as Color,
                        modifier = Modifier.size(16.dp)
                    )
                }
            }
        }
    }
}
